import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import type { Id } from '@cvx/_generated/dataModel'
import { Button } from '@rekrytering/ui/components/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@rekrytering/ui/components/select'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'

type HiringRole = 'recruiter' | 'hiring_manager' | 'interviewer'

const ROLE_LABELS: Record<HiringRole, string> = {
  recruiter: 'Recruiter',
  hiring_manager: 'Hiring Manager',
  interviewer: 'Interviewer',
}

type Props = {
  readonly jobId: Id<'jobs'>
}

export const HiringTeamEditor = ({ jobId }: Props) => {
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>(
    undefined,
  )
  const [selectedRole, setSelectedRole] = useState<HiringRole>('recruiter')

  const { data: teamMembers = [] } = useQuery(
    convexQuery(api.features.jobs.hiring_team.listHiringTeamForMyJob, {
      jobId,
    }),
  )

  const { data: orgMembers = [] } = useQuery(
    convexQuery(api.features.team.team.listMyTeamMembers, {}),
  )

  const { mutateAsync: addMember } = useMutation({
    mutationFn: useConvexMutation(
      api.features.jobs.hiring_team.addMemberToMyHiringTeam,
    ),
  })

  const { mutateAsync: removeMember } = useMutation({
    mutationFn: useConvexMutation(
      api.features.jobs.hiring_team.removeMemberFromMyHiringTeam,
    ),
  })

  const assignedUserIds = new Set(teamMembers.map((m) => m.userId))
  const availableMembers = orgMembers.filter(
    (m) => !assignedUserIds.has(m.userId),
  )

  const getUserLabel = (userId: string): string => {
    const member = orgMembers.find((m) => m.userId === userId)
    if (!member) return userId
    if (member.firstName || member.lastName) {
      return [member.firstName, member.lastName].filter(Boolean).join(' ')
    }
    return member.username ?? member.email
  }

  const handleAdd = async (): Promise<void> => {
    if (!selectedUserId) return
    await addMember({ jobId, userId: selectedUserId, role: selectedRole })
    setSelectedUserId(undefined)
  }

  return (
    <div className="flex flex-col gap-4">
      <h3 className="text-sm font-medium text-primary/70">Hiring team</h3>

      {teamMembers.length === 0 ? (
        <p className="text-sm text-primary/40">No hiring team members yet.</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {teamMembers.map((member) => (
            <li
              key={member._id}
              className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2"
            >
              <div className="flex flex-col gap-0.5">
                <span className="text-sm text-primary">
                  {getUserLabel(member.userId)}
                </span>
                <span className="text-xs text-primary/50">
                  {ROLE_LABELS[member.role]}
                </span>
              </div>
              <Button
                size="sm"
                variant="ghost"
                onClick={() =>
                  void removeMember({ jobId, userId: member.userId })
                }
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      )}

      {availableMembers.length > 0 && (
        <div className="flex items-center gap-2">
          <Select
            value={selectedUserId ?? ''}
            onValueChange={(value) => setSelectedUserId(value ?? undefined)}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Select member..." />
            </SelectTrigger>
            <SelectContent>
              {availableMembers.map((member) => (
                <SelectItem key={member.userId} value={member.userId}>
                  {getUserLabel(member.userId)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={selectedRole}
            onValueChange={(value) => setSelectedRole(value as HiringRole)}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(ROLE_LABELS) as HiringRole[]).map((role) => (
                <SelectItem key={role} value={role}>
                  {ROLE_LABELS[role]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Button
            size="sm"
            onClick={() => void handleAdd()}
            disabled={!selectedUserId}
          >
            Add
          </Button>
        </div>
      )}
    </div>
  )
}
